import { useEffect, useEffectEvent, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import { IRole, TrainingParticipantsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useTrainingLinkedGroups } from 'training/hooks/useTrainingLinkedGroups';
import { useUserFilter } from 'user/hooks/useUserFilter';

export const useTrainingParticipantList = (trainingId: string) => {
    const { showApolloError } = useApolloError();

    const filterPermissions = {
        rolesFilter: [IRole.Owner, IRole.Manager, IRole.Author, IRole.Trainer],
    };

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    const { linkedGroupIds: groupPriorityIds } = useTrainingLinkedGroups({
        trainingId,
    });

    const {
        filterValues,
        filters,
        filterValuesForQuery,
        loading: filtersLoading,
        onSearch,
        onFilterChange,
    } = useUserFilter(filterPermissions, false, groupPriorityIds);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const userQueryVariables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
        }),
        [filterValuesForQuery]
    );

    const {
        loading: participantsLoading,
        data,
        error,
    } = useQuery(TrainingParticipantsDocument, {
        variables: {
            id: trainingId,
            ...userQueryVariables,
            archived: false,
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const loading = filtersLoading || participantsLoading;

    const trainingParticipants = data?.training?.participants;

    // Because training users meta setup has more information in the edges we don't get the nodes
    const items = trainingParticipants?.edges || [];
    const count = trainingParticipants?.count || 0;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(count || 0);
    }, [count, loading, initializePagination]);

    return {
        participants: items,
        count,
        loading,
        paginationSettings,
        filters,
        filterValues,
        filterValuesForQuery,
        onSearch,
        onFilterChange,
        setPage,
    };
};
